import {useState, useEffect, useRef} from 'react';

import {format, formatDistanceToNowStrict} from 'date-fns';
import {useParams, useNavigate, useLocation, useSearchParams} from 'react-router-dom';
import {useForm, useFieldArray} from 'react-hook-form';
import {zodResolver} from '@hookform/resolvers/zod';
import {z} from 'zod';

import {Card, CardContent, CardHeader, CardTitle} from '@common-web/components/ui/card';
import {Button} from '@common-web/components/ui/button';
import {Badge} from '@common-web/components/ui/badge';
import {Textarea} from '@common-web/components/ui/textarea';
import {Input} from '@common-web/components/ui/input';
import {Label} from '@common-web/components/ui/label';
import {Alert, AlertDescription, AlertTitle} from '@common-web/components/ui/alert';
import {Tabs, TabsContent, TabsList, TabsTrigger} from '@common-web/components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@common-web/components/ui/alert-dialog';
import {Skeleton} from '@common-web/components/ui/skeleton';
import {Separator} from '@common-web/components/ui/separator';
import {PageHeader, PageHeaderAction} from '@common-web/components/page-header';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@common-web/components/ui/select';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@common-web/components/ui/form';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@common-web/components/ui/dropdown-menu';
import {Dialog, DialogContent, DialogHeader, DialogTitle} from '@common-web/components/ui/dialog';

import {
  CheckCircle,
  XCircle,
  User,
  Calendar,
  FileText,
  Loader2,
  AlertCircle,
  Building,
  Copy,
  MoreVertical,
  Pencil,
  Plus,
  RefreshCcw,
  Save,
  Send,
  X,
  Archive as ArchiveIcon,
  ArchiveRestore,
  Trash2,
  Sparkles,
  Link2,
  KeyRound,
  Clock,
  Share2,
  Mail,
  Rocket,
  ExternalLink,
} from 'lucide-react';

import {RfcHistory} from '@common-web/components/rfc/RfcHistory';
import {ProspectHistory} from '@common-web/components/prospect/ProspectHistory';
import {RfcReadOnlyView} from '@common-web/components/rfc/RfcReadOnlyView';
import {RfcFileList} from '@common-web/components/rfc/RfcFileList';
import {
  getRfcStatusDisplayName,
  getStatusColor,
  getStatusIcon,
} from '@common-web/components/rfc/RfcStatus';
import {RfcGeneratePdfButton} from '@common-web/components/rfc/RfcPdfExport';
import {RfcGenerateCsvButton} from '@common-web/components/rfc/RfcCsvExport';
import {OnboardingPipelineCard} from '@common-web/components/rfc/OnboardingPipelineCard';
import {AdminRfcEditor} from '@client/pages/rfc/AdminRfcEditor';

import {toast} from '@common-web/hooks/use-toast';
import {useFissionMessage} from '@common-web/hooks/use-fission-message';
import {createMutationErrorHandler, trpc} from '@common-web/lib/trpc';
import {useAuthStore} from '@common-web/stores/authStore';

import {RfcAction, RfcAttachmentType, RfcStatus} from '@common/types/rfc';
import {ProspectAction} from '@common/types/prospect';
import {getBlankInternalRfcFormState, RfcInternalDataSchema} from '@common/lib/rfcSchema';
import {generateRandomPassword} from '@common/lib/utils';
import {resolveRfcAchAuthorizationEnabled} from '@common/menuFlags';
import {CODE_PERMISSIONS} from '@common/permissions';
import {useClientActionAllowed, useHasPermission} from '@common-web/hooks/use-permissions';

type InternalData = z.infer<typeof RfcInternalDataSchema>;

export function ClientRfcReview() {
  const {rfcId} = useParams<{rfcId: string}>();
  const navigate = useNavigate();
  const location = useLocation();
  const isInternalUser = useAuthStore((state) => state.isInternalUser());
  const isBrokerUser = useAuthStore((state) => state.isBrokerUser());
  const user = useAuthStore((state) => state.user);
  // Display vs capability: `rfc.view_pepm` only controls PEPM visibility;
  // the workflow UI (pipeline cards vs the NextSteps fallback) keys off
  // `workflow.approve_rfc` so custom PEPM-only roles don't affect it.
  const canViewPepm = useHasPermission(CODE_PERMISSIONS.rfc.viewPepm.key);
  const canManageRfc = useHasPermission(CODE_PERMISSIONS.workflow.approveRfc.key);
  // The AdminRfcEditor saves via clients.rfc.updateFormAdmin, which enforces
  // `rfc.edit` for client-profile callers — hide the Edit RFC entry point for
  // roles that would only fail at save. Brokers/internal pass by profile.
  const canEditRfc = useClientActionAllowed(CODE_PERMISSIONS.rfc.edit.key);
  const isClientAdmin = !!user?.clientProfile && canManageRfc;
  const showPepm = isInternalUser || (!!user?.clientProfile && canViewPepm);
  const locationState = location.state as {password?: string} | null;

  const [notes, setNotes] = useState('');
  const [confirmAction, setConfirmAction] = useState<'APPROVE' | 'REJECT' | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [editing, setEditing] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const defaultTab = isInternalUser ? 'internal' : 'details';
  const allowedTabs = new Set([
    'internal',
    'details',
    'attachments',
    'proposal',
    'notes',
    'history',
    'summary',
  ]);
  const initialTab = (() => {
    const qp = searchParams.get('tab');
    return qp && allowedTabs.has(qp) ? qp : defaultTab;
  })();
  const [activeTab, setActiveTabState] = useState(initialTab);
  const setActiveTab = (next: string) => {
    setActiveTabState(next);
    setSearchParams(
      (prev) => {
        if (next === defaultTab) {
          prev.delete('tab');
        } else {
          prev.set('tab', next);
        }
        return prev;
      },
      {replace: true},
    );
  };

  // Access link state
  const [newPassword, setNewPassword] = useState(locationState?.password || '');
  const [copied, setCopied] = useState(false);
  const [shareLinkOpen, setShareLinkOpen] = useState(!!locationState?.password);
  const [shareLinkCopied, setShareLinkCopied] = useState(false);

  // Internal-only state
  const [processingNotes, setProcessingNotes] = useState('');
  const [completionNotes, setCompletionNotes] = useState('');
  const [confirmArchiveOpen, setConfirmArchiveOpen] = useState(false);
  const [confirmUnarchiveOpen, setConfirmUnarchiveOpen] = useState(false);

  // MSA state
  const [msaTemplateId, setMsaTemplateId] = useState('');
  const [msaSignerEmail, setMsaSignerEmail] = useState('');
  const [msaSignerName, setMsaSignerName] = useState('');
  const [msaPreFilled, setMsaPreFilled] = useState(false);

  // Manual MSA state
  const [msaManualUrl, setMsaManualUrl] = useState('');
  const [msaManualNote, setMsaManualNote] = useState('');

  // Notes state
  const [newNote, setNewNote] = useState('');
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editingNoteContent, setEditingNoteContent] = useState('');

  const rfcQuery = trpc.clients.rfc.getById.useQuery({rfcId: rfcId!}, {enabled: !!rfcId});

  // Client adjudication
  const adjudicateMutation = trpc.clients.rfc.adjudicate.useMutation({
    onSuccess: () => {
      rfcQuery.refetch();
      setNotes('');
      toast({title: 'Deal Updated', description: 'The deal has been updated successfully.'});
    },
    onError: createMutationErrorHandler('Failed to submit deal approval'),
  });

  // Broker-step approve / broker reject
  const approveMutation = trpc.clients.rfc.approveBrokerStep.useMutation({
    onSuccess: () => {
      rfcQuery.refetch();
      setNotes('');
      toast({
        title: 'Deal Approved',
        description: 'The deal has been approved and forwarded to the client admin for review.',
      });
    },
    onError: createMutationErrorHandler('Failed to approve deal'),
  });

  const rejectMutation = trpc.clients.rfc.brokerReject.useMutation({
    onSuccess: () => {
      rfcQuery.refetch();
      setNotes('');
      toast({
        title: 'Deal Rejected',
        description: 'The deal has been rejected.',
        variant: 'destructive',
      });
    },
    onError: createMutationErrorHandler('Failed to reject deal'),
  });

  // Internal-only mutations
  const internalForm = useForm<InternalData>({
    resolver: zodResolver(RfcInternalDataSchema),
    defaultValues: getBlankInternalRfcFormState(),
  });

  const {
    fields: productCodes,
    append: appendProductCode,
    remove: removeProductCode,
  } = useFieldArray({control: internalForm.control, name: 'productCodes'});

  const updateFormInternalMutation = trpc.clients.rfc.updateFormInternal.useMutation({
    onSuccess: () => rfcQuery.refetch(),
    onError: createMutationErrorHandler('Processing Error'),
  });

  const completeMutation = trpc.clients.rfc.complete.useMutation({
    onSuccess: () => rfcQuery.refetch(),
    onError: createMutationErrorHandler('Completion Error'),
  });

  const regeneratePasswordMutation = trpc.clients.rfc.regeneratePassword.useMutation({
    onError: createMutationErrorHandler('Password Regeneration Error'),
  });

  const archiveMutation = trpc.clients.rfc.archive.useMutation({
    onSuccess: () => navigate(isInternalUser ? '/rfc/admin' : '/rfc'),
    onError: createMutationErrorHandler('Archive Error'),
  });

  const unarchiveMutation = trpc.clients.rfc.unarchive.useMutation({
    onSuccess: () => {
      rfcQuery.refetch();
      toast({title: 'Deal restored', description: 'The deal has been moved back to Draft status.'});
    },
    onError: createMutationErrorHandler('Failed to restore deal'),
  });

  const downloadPdfMutation = trpc.clients.rfc.downloadSubmissionPdf.useMutation({
    onError: createMutationErrorHandler('Download Error'),
  });

  // MSA DocuSign
  const msaTemplatesQuery = trpc.clients.rfc.listDocuSignTemplates.useQuery(
    {rfcId},
    {enabled: !isBrokerUser},
  );

  const sendMsaMutation = trpc.clients.rfc.sendMsa.useMutation({
    onSuccess: () => {
      rfcQuery.refetch();
      toast({title: 'MSA Sent', description: 'The MSA has been sent via DocuSign for signing.'});
    },
    onError: createMutationErrorHandler('Failed to send MSA'),
  });

  const submitMsaManualMutation = trpc.clients.rfc.submitMsaManual.useMutation({
    onSuccess: () => {
      rfcQuery.refetch();
      setMsaManualUrl('');
      setMsaManualNote('');
      toast({title: 'MSA Submitted', description: 'The MSA has been recorded manually.'});
    },
    onError: createMutationErrorHandler('Failed to submit MSA'),
  });

  const notifyCbsMsaSignedMutation = trpc.clients.rfc.notifyCbsMsaSigned.useMutation({
    onSuccess: (data) => {
      toast({
        title: 'CBS Team Notified',
        description: `${data.emailsSent} notification${data.emailsSent !== 1 ? 's' : ''} sent.`,
      });
    },
    onError: createMutationErrorHandler('Failed to notify CBS team'),
  });

  // Notes mutations
  const addNoteMutation = trpc.clients.rfc.addNote.useMutation({
    onSuccess: () => {
      rfcQuery.refetch();
      setNewNote('');
    },
    onError: createMutationErrorHandler('Failed to add note'),
  });
  const updateNoteMutation = trpc.clients.rfc.updateNote.useMutation({
    onSuccess: () => {
      rfcQuery.refetch();
      setEditingNoteId(null);
      setEditingNoteContent('');
    },
    onError: createMutationErrorHandler('Failed to update note'),
  });
  const deleteNoteMutation = trpc.clients.rfc.deleteNote.useMutation({
    onSuccess: () => rfcQuery.refetch(),
    onError: createMutationErrorHandler('Failed to delete note'),
  });

  // Unread note-added notifications for this deal drive the red dot on the
  // Notes tab; opening the tab marks them read so the indicator clears.
  const rfcNoteNotificationsUnreadQuery = trpc.user.notification.getUnreadCount.useQuery(
    {rfcSubmissionId: rfcId!},
    {enabled: !!rfcId},
  );
  const utils = trpc.useUtils();
  const markRfcNoteNotificationsReadMutation = trpc.user.notification.markAllAsRead.useMutation({
    onSuccess: () => {
      rfcNoteNotificationsUnreadQuery.refetch();
      utils.user.notification.getUnreadCount.invalidate();
    },
  });
  const rfcNoteUnreadCount = rfcNoteNotificationsUnreadQuery.data?.count ?? 0;

  useEffect(() => {
    if (!rfcId) return;
    if (activeTab !== 'notes') return;
    if (rfcNoteUnreadCount === 0) return;
    markRfcNoteNotificationsReadMutation.mutate({rfcSubmissionId: rfcId});
  }, [activeTab, rfcId, rfcNoteUnreadCount]);

  useEffect(() => {
    if (rfcQuery.data && isInternalUser) {
      internalForm.reset(rfcQuery.data.internalData || getBlankInternalRfcFormState());
    }
  }, [rfcQuery.data, isInternalUser]);

  // Pre-fill MSA signer from authorized signer contact
  useEffect(() => {
    if (msaPreFilled || !rfcQuery.data) return;
    const rfc = rfcQuery.data;
    const contacts = rfc.section125FormData?.contacts || rfc.mecFormData?.contacts;
    if (contacts?.authorizedSigner) {
      const signer = contacts.authorizedSigner;
      const name = [signer.firstName, signer.lastName].filter(Boolean).join(' ');
      if (name && !msaSignerName) setMsaSignerName(name);
      if (signer.email && !msaSignerEmail) setMsaSignerEmail(signer.email);
      setMsaPreFilled(true);
    }
  }, [rfcQuery.data, msaPreFilled]);

  const rfc = rfcQuery.data;
  const rfcStatus = rfc?.status as RfcStatus | undefined;
  const internalData = rfc?.internalData as InternalData | undefined;

  // Determine who can take which actions. Client adjudication requires
  // `workflow.approve_rfc` — the server enforces the same key on
  // `rfc.adjudicate`, so viewers without it never see buttons that would
  // only 403.
  const clientCanAdjudicate =
    !isInternalUser &&
    !isBrokerUser &&
    canManageRfc &&
    (rfcStatus === RfcStatus.SUBMITTED ||
      rfcStatus === RfcStatus.CLIENT_REVIEW ||
      rfcStatus === RfcStatus.BROKER_APPROVED);
  const brokerCanReview = isBrokerUser && rfcStatus === RfcStatus.BROKER_REVIEW;
  // Client admins may complete the broker-review step on the broker's behalf
  // (e.g. an unresponsive broker). Approve-only — rejection stays with the
  // broker. Server enforces the same `workflow.approve_rfc` key on
  // `rfc.approveBrokerStep` for client-profile callers. Mutually exclusive
  // with `clientCanAdjudicate` (BROKER_REVIEW is not an adjudicable status).
  const clientCanApproveForBroker =
    !isInternalUser && !isBrokerUser && canManageRfc && rfcStatus === RfcStatus.BROKER_REVIEW;
  const canProcess =
    isInternalUser &&
    (rfcStatus === RfcStatus.CLIENT_APPROVED ||
      rfcStatus === RfcStatus.CBS_PROCESSING ||
      rfcStatus === RfcStatus.MSA_SIGNED);
  const canComplete = isInternalUser && rfcStatus === RfcStatus.CBS_PROCESSING;
  const canSendMsa = !isBrokerUser && rfcStatus === RfcStatus.MSA_PENDING;
  const isMsaSent = rfcStatus === RfcStatus.MSA_SENT;
  const isCompleted = rfcStatus === RfcStatus.COMPLETED;

  const isPending =
    adjudicateMutation.isPending || approveMutation.isPending || rejectMutation.isPending;

  const onClientAdjudicate = async (action: 'APPROVE' | 'REJECT') => {
    if (!rfcId) return;
    if (action === 'REJECT' && !notes.trim()) {
      toast({
        title: 'Notes Required',
        description: 'Please fill out the rejection reason.',
        variant: 'destructive',
      });
      return;
    }
    await adjudicateMutation.mutateAsync({rfcId, action, notes: notes.trim()});
  };

  const onBrokerAction = async (action: 'APPROVE' | 'REJECT') => {
    if (!rfcId) return;
    if (action === 'REJECT' && !notes.trim()) {
      toast({
        title: 'Notes Required',
        description: 'Please provide a reason for rejection.',
        variant: 'destructive',
      });
      return;
    }
    if (action === 'APPROVE') {
      await approveMutation.mutateAsync({rfcId, notes: notes.trim() || undefined});
    } else {
      await rejectMutation.mutateAsync({rfcId, notes: notes.trim()});
    }
  };

  const handleSaveInternalData = async () => {
    if (!rfcId) return;
    await updateFormInternalMutation.mutateAsync({
      rfcId,
      internalData: internalForm.getValues(),
      notes: processingNotes || '',
    });
    setProcessingNotes('');
  };

  const handleComplete = async () => {
    if (!rfcId) return;
    await completeMutation.mutateAsync({rfcId, notes: completionNotes || undefined});
    setCompletionNotes('');
  };

  const handleSendMsa = async () => {
    if (!rfcId || !msaTemplateId || !msaSignerEmail.trim() || !msaSignerName.trim()) return;
    await sendMsaMutation.mutateAsync({
      rfcId,
      templateId: msaTemplateId,
      signerEmail: msaSignerEmail.trim(),
      signerName: msaSignerName.trim(),
    });
  };

  const handleSubmitMsaManual = async () => {
    if (!rfcId || !msaManualNote.trim()) return;
    await submitMsaManualMutation.mutateAsync({
      rfcId,
      url: msaManualUrl.trim() || undefined,
      note: msaManualNote.trim(),
    });
  };

  // ACH completeness check - ACH is now per-location inside groupInfo
  const activeFormData = (
    rfc?.section125FormEnabled ? rfc?.section125FormData : rfc?.mecFormData
  ) as {
    groupInfo?: {
      locations?: Array<{
        locationName?: string;
        achForm?: {
          achDetails?: {routingNumber?: string; accountNumber?: string; bankName?: string};
        };
      }>;
    };
  } | null;
  const isAchIncomplete =
    activeFormData?.groupInfo?.locations?.some(
      (loc) =>
        !loc.achForm?.achDetails?.routingNumber ||
        !loc.achForm?.achDetails?.accountNumber ||
        !loc.achForm?.achDetails?.bankName,
    ) ?? true;

  const handleRegeneratePassword = async () => {
    if (!rfcId) return;
    const password = generateRandomPassword();
    await regeneratePasswordMutation.mutateAsync({rfcId, password});
    setNewPassword(password);
  };

  const accessUrl =
    rfc?.client?.domain && rfc?.accessToken
      ? `https://${rfc.client.domain}/rfc/${rfc.accessToken}`
      : '';

  const handleCopyAccessUrl = async () => {
    if (!accessUrl) return;
    await navigator.clipboard.writeText(accessUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const clientName = rfc?.client?.name || '';

  return (
    <div className='min-h-screen bg-gradient-to-br from-gray-50 via-white to-brand-blue/5'>
      <div className='flex flex-col gap-0'>
        {/* Header */}
        <PageHeader
          title={
            isInternalUser ? 'Deal Administration' : isBrokerUser ? 'Deal Review' : 'Review Deal'
          }
          description={rfc?.client?.name || 'Review and manage the deal submission'}
          icon={<FileText className='h-6 w-6' />}
          isFetching={rfcQuery.isFetching}
          isLoading={rfcQuery.isLoading}
        >
          {rfc && rfcStatus && (
            <>
              <div className='flex items-center gap-2 text-white'>
                {/*{getStatusIcon(rfcStatus)}*/}
                <Badge variant={getStatusColor(rfcStatus)}>
                  {getRfcStatusDisplayName(rfcStatus)}
                </Badge>
              </div>
              <RfcGeneratePdfButton
                clientName={rfc.client.name}
                section125FormEnabled={!!rfc.section125FormEnabled}
                mecFormEnabled={!!rfc.mecFormEnabled}
                section125Form={rfc.section125FormData}
                mecForm={rfc.mecFormData}
                internalData={isInternalUser ? internalData : undefined}
                includeInternalFields={isInternalUser}
                showPepm={showPepm}
                clientPortalAccessForm={rfc.clientPortalAccessData}
                achEnabled={resolveRfcAchAuthorizationEnabled(rfc.client.features, rfc.client.name)}
              />
              <RfcGenerateCsvButton
                clientName={rfc.client.name}
                section125FormEnabled={!!rfc.section125FormEnabled}
                mecFormEnabled={!!rfc.mecFormEnabled}
                section125Form={rfc.section125FormData}
                mecForm={rfc.mecFormData}
                internalData={isInternalUser ? internalData : undefined}
                includeInternalFields={isInternalUser}
                showPepm={showPepm}
                clientPortalAccessForm={rfc.clientPortalAccessData}
              />
            </>
          )}
          {rfcStatus === RfcStatus.ARCHIVED && (
            <>
              <PageHeaderAction
                tone='secondary'
                onClick={() => setConfirmUnarchiveOpen(true)}
                disabled={unarchiveMutation.isPending}
              >
                {unarchiveMutation.isPending ? (
                  <Loader2 className='h-4 w-4 mr-2 animate-spin' />
                ) : (
                  <ArchiveRestore className='h-4 w-4 mr-2' />
                )}
                Restore from Archive
              </PageHeaderAction>
              <AlertDialog open={confirmUnarchiveOpen} onOpenChange={setConfirmUnarchiveOpen}>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Restore this deal?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This will restore the deal back to the active pipeline with Draft status. Do
                      you want to continue?
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel disabled={unarchiveMutation.isPending}>
                      Cancel
                    </AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => rfcId && unarchiveMutation.mutateAsync({rfcId})}
                      disabled={unarchiveMutation.isPending}
                    >
                      {unarchiveMutation.isPending ? 'Restoring...' : 'Restore'}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </>
          )}
          {isInternalUser && rfcStatus !== RfcStatus.ARCHIVED && (
            <>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant='outline'
                    size='icon'
                    className='border-white/20 text-white hover:bg-white/10'
                  >
                    <MoreVertical className='h-4 w-4' />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align='end'>
                  <DropdownMenuItem
                    className='text-red-600'
                    onSelect={(e) => {
                      e.preventDefault();
                      setConfirmArchiveOpen(true);
                    }}
                  >
                    <ArchiveIcon className='h-4 w-4' />
                    Archive Deal
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <AlertDialog open={confirmArchiveOpen} onOpenChange={setConfirmArchiveOpen}>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Archive this deal?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Archiving will remove this deal from the portal, making it inaccessible. Do
                      you want to continue?
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel disabled={archiveMutation.isPending}>
                      Cancel
                    </AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => rfcId && archiveMutation.mutateAsync({rfcId})}
                      disabled={archiveMutation.isPending}
                    >
                      {archiveMutation.isPending ? 'Archiving...' : 'Archive'}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </>
          )}
        </PageHeader>

        {/* Main Content */}
        <div className='px-8 py-6 max-w-7xl mx-auto w-full'>
          {rfcQuery.isLoading ? (
            <div className='grid grid-cols-1 lg:grid-cols-3 gap-6'>
              <div className='lg:col-span-2 space-y-6'>
                <Skeleton className='h-12 w-full' />
                <Skeleton className='h-64 w-full' />
              </div>
              <div className='space-y-4'>
                <Skeleton className='h-40 w-full' />
                <Skeleton className='h-40 w-full' />
              </div>
            </div>
          ) : rfcQuery.error || !rfc ? (
            <Alert variant='destructive'>
              <AlertCircle className='h-4 w-4' />
              <AlertTitle>Error loading deal</AlertTitle>
              <AlertDescription>{rfcQuery.error?.message || 'Deal not found'}</AlertDescription>
            </Alert>
          ) : (
            <>
              {/* Status info banner */}
              {!clientCanAdjudicate && !brokerCanReview && !canProcess && (
                <StatusInfoBanner
                  status={rfcStatus}
                  isInternalUser={isInternalUser}
                  isBrokerUser={isBrokerUser}
                />
              )}
              {/* ACH incomplete warning */}
              {isAchIncomplete &&
                (rfcStatus === RfcStatus.MSA_SIGNED || rfcStatus === RfcStatus.CBS_PROCESSING) && (
                  <Alert className='mb-6 border-amber/30 bg-amber/10'>
                    <AlertCircle className='h-5 w-5 text-amber' />
                    <AlertTitle className='text-amber-900 font-semibold'>
                      ACH Information Incomplete
                    </AlertTitle>
                    <AlertDescription className='text-amber-800'>
                      One or more locations are missing ACH banking details (bank name, routing
                      number, and account number). All locations must have ACH information completed
                      before this deal can be sent to CBS for processing. Use the "Edit RFC" button
                      on the Client Data tab to complete the ACH forms.
                    </AlertDescription>
                  </Alert>
                )}
              <div
                className={`grid grid-cols-1 ${activeTab === 'proposal' ? '' : isInternalUser ? 'xl:grid-cols-6' : 'lg:grid-cols-3'} gap-6`}
              >
                {/* Main content area */}
                <div className={isInternalUser ? 'xl:col-span-4' : 'lg:col-span-2'}>
                  <Tabs
                    defaultValue={activeTab}
                    value={activeTab}
                    onValueChange={setActiveTab}
                    className='w-full'
                  >
                    <TabsList
                      className={
                        isInternalUser
                          ? 'bg-gray-100 p-1 grid w-full grid-cols-6'
                          : 'bg-gray-100 p-1'
                      }
                    >
                      {isInternalUser && (
                        <TabsTrigger
                          value='internal'
                          className='data-[state=active]:bg-white data-[state=active]:text-brand-navy data-[state=active]:shadow-sm'
                        >
                          Internal Fields
                        </TabsTrigger>
                      )}
                      <TabsTrigger
                        value='details'
                        className='data-[state=active]:bg-white data-[state=active]:text-brand-navy data-[state=active]:shadow-sm'
                      >
                        {isInternalUser ? 'Client Data' : 'Submission Details'}
                      </TabsTrigger>
                      <TabsTrigger
                        value='attachments'
                        className='data-[state=active]:bg-white data-[state=active]:text-brand-navy data-[state=active]:shadow-sm'
                      >
                        Files ({rfc.attachments.length})
                      </TabsTrigger>
                      {!isInternalUser && rfc.client.fissionTenantCode && (
                        <TabsTrigger
                          value='proposal'
                          className='data-[state=active]:bg-white data-[state=active]:text-brand-navy data-[state=active]:shadow-sm'
                        >
                          Proposal ({rfc.proposals.length})
                        </TabsTrigger>
                      )}
                      <TabsTrigger
                        value='notes'
                        className='relative data-[state=active]:bg-white data-[state=active]:text-brand-navy data-[state=active]:shadow-sm'
                      >
                        Notes ({rfc.notes?.length || 0})
                        {rfcNoteUnreadCount > 0 && (
                          <span
                            aria-label={`${rfcNoteUnreadCount} unread note notification${rfcNoteUnreadCount === 1 ? '' : 's'}`}
                            className='ml-2 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1.5 text-[10px] font-bold text-white'
                          >
                            {rfcNoteUnreadCount}
                          </span>
                        )}
                      </TabsTrigger>
                      <TabsTrigger
                        value='history'
                        className='data-[state=active]:bg-white data-[state=active]:text-brand-navy data-[state=active]:shadow-sm'
                      >
                        {isInternalUser ? 'Audit Trail' : 'History'}
                      </TabsTrigger>
                      {isInternalUser && (
                        <TabsTrigger
                          value='summary'
                          className='data-[state=active]:bg-white data-[state=active]:text-brand-navy data-[state=active]:shadow-sm'
                        >
                          Summary
                        </TabsTrigger>
                      )}
                    </TabsList>

                    {/* Internal Fields Tab */}
                    {isInternalUser && (
                      <TabsContent value='internal' className='space-y-6'>
                        <InternalFieldsTab
                          rfcId={rfcId!}
                          rfcStatus={rfcStatus!}
                          internalData={internalData}
                          internalForm={internalForm}
                          productCodes={productCodes}
                          appendProductCode={appendProductCode}
                          removeProductCode={removeProductCode}
                          canProcess={!!canProcess}
                          isCompleted={!!isCompleted}
                          processingNotes={processingNotes}
                          setProcessingNotes={setProcessingNotes}
                          onSave={handleSaveInternalData}
                          isSaving={updateFormInternalMutation.isPending}
                        />
                      </TabsContent>
                    )}

                    {/* Details Tab */}
                    <TabsContent value='details' className='space-y-4'>
                      {editing ? (
                        <div className='space-y-4'>
                          <div className='flex justify-between'>
                            <div className='font-semibold text-brand-navy'>Editing RFC</div>
                            <Button
                              variant='outline'
                              size='sm'
                              onClick={() => setEditing(false)}
                              className='transition-colors'
                            >
                              Cancel
                            </Button>
                          </div>
                          <AdminRfcEditor
                            rfcId={rfcId!}
                            onCancel={() => setEditing(false)}
                            onSaved={async () => {
                              await rfcQuery.refetch();
                              setEditing(false);
                            }}
                          />
                        </div>
                      ) : (
                        <div className='space-y-4'>
                          <div className='flex items-center justify-between mt-4 gap-4'>
                            {isBrokerUser && (
                              <Alert className='border-brand-blue/20 bg-brand-blue/5 flex-1'>
                                <AlertDescription className='text-brand-navy'>
                                  To submit the RFC for approval, you must use the Deal Access Link.
                                </AlertDescription>
                              </Alert>
                            )}
                            {canEditRfc && (
                              <Button
                                size='sm'
                                onClick={() => setEditing(true)}
                                className='bg-brand-blue hover:bg-brand-blue-dark text-white font-semibold shadow-sm transition-colors shrink-0 ml-auto'
                              >
                                Edit RFC
                              </Button>
                            )}
                          </div>
                          <RfcReadOnlyView
                            data={{
                              section125FormEnabled: !!rfc.section125FormEnabled,
                              mecFormEnabled: !!rfc.mecFormEnabled,
                              section125Form: rfc.section125FormData,
                              mecForm: rfc.mecFormData,
                              clientPortalAccessForm: rfc.clientPortalAccessData,
                            }}
                            showEnrollmentType={isInternalUser}
                            showPepm={showPepm}
                            showClientPortalAccess={rfc.clientPortalAccessEnabled}
                          />
                        </div>
                      )}
                    </TabsContent>

                    {/* Files Tab */}
                    <TabsContent value='attachments'>
                      <RfcFileList
                        attachments={rfc.attachments.map((a) => ({
                          ...a,
                          fileType: a.fileType as RfcAttachmentType,
                          createdAt: new Date(a.createdAt).toISOString(),
                        }))}
                        showAdminActions={!isBrokerUser}
                        showDownloadLinks={true}
                        refreshList={() => rfcQuery.refetch()}
                      />
                    </TabsContent>

                    {/* Proposal Tab */}
                    {!isInternalUser && rfc.client.fissionTenantCode && (
                      <TabsContent value='proposal'>
                        <ProposalTab
                          rfcId={rfcId!}
                          proposals={rfc.proposals}
                          companyName={
                            rfc.companyName ||
                            rfc.section125FormData?.groupInfo?.companyName ||
                            rfc.mecFormData?.groupInfo?.companyName ||
                            'your company'
                          }
                          onProposalSaved={() => rfcQuery.refetch()}
                        />
                      </TabsContent>
                    )}

                    {/* Notes Tab */}
                    <TabsContent value='notes' className='space-y-4'>
                      <Card>
                        <CardContent className='pt-6'>
                          <div className='space-y-3'>
                            <Textarea
                              value={newNote}
                              onChange={(e) => setNewNote(e.target.value)}
                              placeholder='Add a note about this deal...'
                              rows={3}
                            />
                            <div className='flex justify-end'>
                              <Button
                                onClick={() => {
                                  if (!rfcId || !newNote.trim()) return;
                                  addNoteMutation.mutate({rfcId, content: newNote.trim()});
                                }}
                                disabled={addNoteMutation.isPending || !newNote.trim()}
                                size='sm'
                              >
                                {addNoteMutation.isPending ? (
                                  <Loader2 className='h-4 w-4 mr-1 animate-spin' />
                                ) : null}
                                Add Note
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                      {rfc.notes?.length === 0 && (
                        <p className='text-sm text-brand-gray text-center py-8'>
                          No notes yet. Add the first note above.
                        </p>
                      )}
                      {rfc.notes?.map((note) => (
                        <Card key={note.id}>
                          <CardContent className='pt-4'>
                            <div className='flex items-start justify-between mb-2'>
                              <div className='text-sm'>
                                <span className='font-medium text-brand-navy'>
                                  {note.createdByUser?.name || 'Unknown'}
                                </span>
                                <span className='text-brand-gray ml-2'>
                                  {format(new Date(note.createdAt), 'MMM d, yyyy h:mm a')}
                                </span>
                              </div>
                              {(!isBrokerUser || note.createdByUser?.id === user?.id) && (
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button variant='ghost' size='icon' className='h-6 w-6'>
                                      <MoreVertical className='h-3 w-3' />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align='end'>
                                    <DropdownMenuItem
                                      onClick={() => {
                                        setEditingNoteId(note.id);
                                        setEditingNoteContent(note.content);
                                      }}
                                    >
                                      <Pencil className='h-4 w-4 mr-2' />
                                      Edit
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      onClick={() => {
                                        if (!rfcId) return;
                                        deleteNoteMutation.mutate({rfcId, noteId: note.id});
                                      }}
                                    >
                                      <Trash2 className='h-4 w-4 mr-2' />
                                      Delete
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              )}
                            </div>
                            {editingNoteId === note.id ? (
                              <div className='space-y-2'>
                                <Textarea
                                  value={editingNoteContent}
                                  onChange={(e) => setEditingNoteContent(e.target.value)}
                                  rows={3}
                                />
                                <div className='flex gap-2 justify-end'>
                                  <Button
                                    variant='outline'
                                    size='sm'
                                    onClick={() => {
                                      setEditingNoteId(null);
                                      setEditingNoteContent('');
                                    }}
                                  >
                                    Cancel
                                  </Button>
                                  <Button
                                    size='sm'
                                    onClick={() => {
                                      if (!rfcId || !editingNoteContent.trim()) return;
                                      updateNoteMutation.mutate({
                                        rfcId,
                                        noteId: note.id,
                                        content: editingNoteContent.trim(),
                                      });
                                    }}
                                    disabled={
                                      updateNoteMutation.isPending || !editingNoteContent.trim()
                                    }
                                  >
                                    Save
                                  </Button>
                                </div>
                              </div>
                            ) : (
                              <p className='text-sm whitespace-pre-wrap'>{note.content}</p>
                            )}
                          </CardContent>
                        </Card>
                      ))}
                    </TabsContent>

                    {/* History Tab */}
                    <TabsContent value='history' className='space-y-4'>
                      <RfcHistory
                        history={rfc.history.map((h) => ({
                          ...h,
                          action: h.action as RfcAction,
                          createdAt: new Date(h.createdAt).toISOString(),
                        }))}
                        showInternalActions={isInternalUser}
                        onDownloadPdf={async (historyId) => {
                          const result = await downloadPdfMutation.mutateAsync({
                            rfcId: rfc.id,
                            historyId,
                          });
                          window.open(result.downloadUrl, '_blank');
                        }}
                      />
                      {rfc.convertedFromProspect && (
                        <ProspectHistory
                          history={rfc.convertedFromProspect.history.map((h) => ({
                            ...h,
                            action: h.action as ProspectAction,
                            createdAt: new Date(h.createdAt).toISOString(),
                          }))}
                        />
                      )}
                    </TabsContent>

                    {/* Summary Tab (Internal only) */}
                    {isInternalUser && (
                      <TabsContent value='summary'>
                        <SummaryTab rfc={rfc} rfcStatus={rfcStatus!} />
                      </TabsContent>
                    )}
                  </Tabs>
                </div>

                {/* Sidebar */}
                <div
                  className={`space-y-4 ${isInternalUser ? 'xl:col-span-2' : ''} ${activeTab === 'proposal' ? 'hidden' : ''}`}
                >
                  {/* Submission Info */}
                  <Card className='border-gray-200 shadow-sm'>
                    <CardHeader className='bg-gray-50 border-b'>
                      <CardTitle className='text-lg font-semibold text-brand-navy'>
                        {isBrokerUser ? 'Deal Info' : 'Submission Info'}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className='space-y-4 p-6'>
                      {isBrokerUser && (
                        <div className='flex items-center gap-2'>
                          <Building className='h-4 w-4 text-brand-gray' />
                          <div>
                            <p className='text-sm font-medium text-brand-navy'>Company</p>
                            <p className='text-sm text-brand-gray'>
                              {rfc.companyName ||
                                rfc.section125FormData?.groupInfo?.companyName ||
                                rfc.mecFormData?.groupInfo?.companyName ||
                                'Not specified'}
                            </p>
                          </div>
                        </div>
                      )}
                      <div className='flex items-center gap-2'>
                        <Calendar className='h-4 w-4 text-brand-gray' />
                        <div>
                          <p className='text-sm font-medium text-brand-navy'>Submitted</p>
                          <p className='text-sm text-brand-gray'>
                            {rfc.submittedAt
                              ? format(new Date(rfc.submittedAt), 'MMM d, yyyy')
                              : 'Not submitted'}
                          </p>
                        </div>
                      </div>
                      {!!rfc.submittedByUser && (
                        <div className='flex items-center gap-2'>
                          <User className='h-4 w-4 text-brand-gray' />
                          <div>
                            <p className='text-sm font-medium text-brand-navy'>Submitted By</p>
                            <p className='text-sm text-brand-gray'>{rfc.submittedByUser.name}</p>
                          </div>
                        </div>
                      )}
                      {isBrokerUser && rfc.client && (
                        <div className='flex items-center gap-2'>
                          <User className='h-4 w-4 text-brand-gray' />
                          <div>
                            <p className='text-sm font-medium text-brand-navy'>Client</p>
                            <p className='text-sm text-brand-gray'>{rfc.client.name}</p>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Broker Info (Client + Internal) */}
                  {!isBrokerUser && rfc.assignedBroker && (
                    <Card className='border-gray-200 shadow-sm'>
                      <CardHeader className='bg-gray-50 border-b'>
                        <CardTitle className='text-lg font-semibold text-brand-navy'>
                          Assigned Broker
                        </CardTitle>
                      </CardHeader>
                      <CardContent className='space-y-4 p-6'>
                        <div className='flex items-center gap-2'>
                          <User className='h-4 w-4 text-brand-gray' />
                          <p className='text-sm font-medium text-brand-navy'>
                            {rfc.assignedBroker.name}
                          </p>
                        </div>
                        {rfc.brokerApprovedAt && (
                          <div className='flex items-center gap-2'>
                            <CheckCircle className='h-4 w-4 text-emerald' />
                            <div>
                              <p className='text-sm font-medium text-brand-navy'>Broker Approved</p>
                              <p className='text-sm text-brand-gray'>
                                {format(new Date(rfc.brokerApprovedAt), 'MMM d, yyyy')}
                              </p>
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  )}

                  {/* Client Review Actions */}
                  {clientCanAdjudicate && (
                    <Card className='border-gray-200 shadow-sm'>
                      <CardHeader className='bg-gray-50 border-b'>
                        <CardTitle className='text-lg font-semibold text-brand-navy'>
                          Review Actions
                        </CardTitle>
                      </CardHeader>
                      <CardContent className='space-y-4 p-6'>
                        <div className='space-y-3'>
                          <Label htmlFor='adjudicationNotes' className='text-brand-navy'>
                            Notes
                          </Label>
                          <Textarea
                            id='adjudicationNotes'
                            placeholder='Enter any notes for the approval or rejection'
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            rows={5}
                            className='border-gray-200 focus:border-brand-blue focus:ring-brand-blue'
                          />
                          <Button
                            onClick={() => {
                              setConfirmAction('APPROVE');
                              setConfirmOpen(true);
                            }}
                            disabled={isPending}
                            className='w-full bg-emerald hover:bg-emerald/90 text-white font-semibold shadow-sm transition-colors'
                            size='sm'
                          >
                            <CheckCircle className='h-4 w-4 mr-2' />
                            Approve Deal
                          </Button>
                          <Button
                            onClick={() => {
                              setConfirmAction('REJECT');
                              setConfirmOpen(true);
                            }}
                            disabled={isPending}
                            className='w-full bg-red-600 hover:bg-red-700 text-white font-semibold shadow-sm transition-colors'
                            size='sm'
                          >
                            <XCircle className='h-4 w-4 mr-2' />
                            Reject Deal
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Broker Review Actions */}
                  {brokerCanReview && (
                    <Card className='border-gray-200 shadow-sm'>
                      <CardHeader className='bg-gray-50 border-b'>
                        <CardTitle className='text-lg font-semibold text-brand-navy'>
                          Review Actions
                        </CardTitle>
                      </CardHeader>
                      <CardContent className='space-y-4 p-6'>
                        <div>
                          <Label htmlFor='reviewNotes' className='text-brand-navy'>
                            Notes
                          </Label>
                          <Textarea
                            id='reviewNotes'
                            placeholder='Enter notes for approval or rejection reason...'
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            rows={4}
                            className='border-gray-200 focus:border-brand-blue focus:ring-brand-blue'
                          />
                        </div>
                        <Button
                          onClick={() => {
                            setConfirmAction('APPROVE');
                            setConfirmOpen(true);
                          }}
                          disabled={isPending}
                          className='w-full bg-emerald hover:bg-emerald/90 text-white font-semibold shadow-sm transition-colors'
                          size='sm'
                        >
                          <CheckCircle className='h-4 w-4 mr-2' />
                          Approve Deal
                        </Button>
                        <Button
                          onClick={() => {
                            setConfirmAction('REJECT');
                            setConfirmOpen(true);
                          }}
                          disabled={isPending}
                          className='w-full bg-red-600 hover:bg-red-700 text-white font-semibold shadow-sm transition-colors'
                          size='sm'
                        >
                          <XCircle className='h-4 w-4 mr-2' />
                          Reject Deal
                        </Button>
                      </CardContent>
                    </Card>
                  )}

                  {/* Client admin approving the broker-review step on the broker's behalf */}
                  {clientCanApproveForBroker && (
                    <Card className='border-gray-200 shadow-sm'>
                      <CardHeader className='bg-gray-50 border-b'>
                        <CardTitle className='text-lg font-semibold text-brand-navy'>
                          Review Actions
                        </CardTitle>
                      </CardHeader>
                      <CardContent className='space-y-4 p-6'>
                        <p className='text-sm text-brand-gray'>
                          This deal is awaiting broker review. As an admin, you can approve it on
                          the broker's behalf to move it forward.
                        </p>
                        <div className='space-y-3'>
                          <Label htmlFor='brokerBehalfNotes' className='text-brand-navy'>
                            Notes
                          </Label>
                          <Textarea
                            id='brokerBehalfNotes'
                            placeholder='Enter any notes for the approval'
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            rows={4}
                            className='border-gray-200 focus:border-brand-blue focus:ring-brand-blue'
                          />
                          <Button
                            onClick={() => {
                              setConfirmAction('APPROVE');
                              setConfirmOpen(true);
                            }}
                            disabled={isPending}
                            className='w-full bg-emerald hover:bg-emerald/90 text-white font-semibold shadow-sm transition-colors'
                            size='sm'
                          >
                            <CheckCircle className='h-4 w-4 mr-2' />
                            Approve on Broker's Behalf
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* MSA: Send via DocuSign or Manual */}
                  {canSendMsa && (
                    <Card className='border-gray-200 shadow-sm'>
                      <CardHeader className='bg-gray-50 border-b'>
                        <CardTitle className='text-lg font-semibold text-brand-navy'>
                          Send MSA for Signing
                        </CardTitle>
                      </CardHeader>
                      <CardContent className='space-y-4 p-6'>
                        {/* Option 1: DocuSign */}
                        <div className='space-y-3'>
                          <div className='text-sm font-semibold text-brand-navy'>
                            Option 1: Send via DocuSign
                          </div>
                          <div className='space-y-2'>
                            <Label className='text-sm text-brand-navy'>DocuSign Template</Label>
                            {msaTemplatesQuery.isLoading ? (
                              <Skeleton className='h-10 w-full' />
                            ) : msaTemplatesQuery.error ? (
                              <Alert variant='destructive'>
                                <AlertDescription>
                                  Failed to load templates: {msaTemplatesQuery.error.message}
                                </AlertDescription>
                              </Alert>
                            ) : !msaTemplatesQuery.data?.length ? (
                              <p className='text-sm text-brand-gray'>
                                No DocuSign templates available.
                              </p>
                            ) : (
                              <Select value={msaTemplateId} onValueChange={setMsaTemplateId}>
                                <SelectTrigger className='border-gray-200 focus:border-brand-blue focus:ring-brand-blue'>
                                  <SelectValue placeholder='Select a template' />
                                </SelectTrigger>
                                <SelectContent>
                                  {msaTemplatesQuery.data.map((t) => (
                                    <SelectItem key={t.templateId} value={t.templateId}>
                                      {t.name}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            )}
                          </div>
                          <div className='space-y-2'>
                            <Label className='text-sm text-brand-navy'>Signer Name</Label>
                            <Input
                              value={msaSignerName}
                              onChange={(e) => setMsaSignerName(e.target.value)}
                              placeholder='Full name of the signer'
                              className='border-gray-200 focus:border-brand-blue focus:ring-brand-blue'
                            />
                          </div>
                          <div className='space-y-2'>
                            <Label className='text-sm text-brand-navy'>Signer Email</Label>
                            <Input
                              type='email'
                              value={msaSignerEmail}
                              onChange={(e) => setMsaSignerEmail(e.target.value)}
                              placeholder='email@company.com'
                              className='border-gray-200 focus:border-brand-blue focus:ring-brand-blue'
                            />
                          </div>
                          <Button
                            onClick={handleSendMsa}
                            disabled={
                              sendMsaMutation.isPending ||
                              !msaTemplateId ||
                              !msaSignerEmail.trim() ||
                              !msaSignerName.trim()
                            }
                            className='w-full bg-brand-blue hover:bg-brand-blue-dark text-white font-semibold shadow-sm transition-colors'
                          >
                            {sendMsaMutation.isPending ? (
                              <Loader2 className='h-4 w-4 mr-2 animate-spin' />
                            ) : (
                              <Send className='h-4 w-4 mr-2' />
                            )}
                            {sendMsaMutation.isPending ? 'Sending...' : 'Send MSA via DocuSign'}
                          </Button>
                        </div>

                        {/* Divider */}
                        <div className='flex items-center gap-3'>
                          <Separator className='flex-1' />
                          <span className='text-sm font-medium text-brand-gray'>OR</span>
                          <Separator className='flex-1' />
                        </div>

                        {/* Option 2: Manual MSA */}
                        <div className='space-y-3'>
                          <div className='text-sm font-semibold text-brand-navy'>
                            Option 2: Submit MSA Manually
                          </div>
                          <div className='space-y-2'>
                            <Label className='text-sm text-brand-navy'>MSA URL (Optional)</Label>
                            <Input
                              value={msaManualUrl}
                              onChange={(e) => setMsaManualUrl(e.target.value)}
                              placeholder='https://...'
                              className='border-gray-200 focus:border-brand-blue focus:ring-brand-blue'
                            />
                          </div>
                          <div className='space-y-2'>
                            <Label className='text-sm text-brand-navy'>Note *</Label>
                            <Textarea
                              value={msaManualNote}
                              onChange={(e) => setMsaManualNote(e.target.value)}
                              placeholder='Any additional details about the MSA...'
                              rows={2}
                              className='border-gray-200 focus:border-brand-blue focus:ring-brand-blue'
                            />
                          </div>
                          <Button
                            onClick={handleSubmitMsaManual}
                            disabled={submitMsaManualMutation.isPending || !msaManualNote.trim()}
                            variant='outline'
                            className='w-full'
                          >
                            {submitMsaManualMutation.isPending ? (
                              <Loader2 className='h-4 w-4 mr-2 animate-spin' />
                            ) : (
                              <Link2 className='h-4 w-4 mr-2' />
                            )}
                            {submitMsaManualMutation.isPending
                              ? 'Submitting...'
                              : 'Submit MSA Manually'}
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* MSA: Status Tracking */}
                  {isMsaSent && rfc && (
                    <Card className='border-gray-200 shadow-sm'>
                      <CardHeader className='bg-gray-50 border-b'>
                        <CardTitle className='text-lg font-semibold text-brand-navy'>
                          MSA Status
                        </CardTitle>
                      </CardHeader>
                      <CardContent className='space-y-4 p-6'>
                        <div className='flex items-center gap-2'>
                          <Loader2 className='h-5 w-5 animate-spin text-brand-blue' />
                          <span className='text-sm font-medium text-brand-navy'>
                            Awaiting Signature
                          </span>
                        </div>
                        <Separator />
                        <div className='space-y-2 text-sm'>
                          {rfc.msaSentAt && (
                            <div className='flex justify-between'>
                              <span className='text-brand-gray'>Sent:</span>
                              <span className='text-brand-navy'>
                                {format(new Date(rfc.msaSentAt), 'MMM d, yyyy h:mm a')}
                              </span>
                            </div>
                          )}
                          {rfc.msaDocuSignStatus && (
                            <div className='flex justify-between'>
                              <span className='text-brand-gray'>DocuSign Status:</span>
                              <Badge variant='outline'>{rfc.msaDocuSignStatus}</Badge>
                            </div>
                          )}
                        </div>
                        <p className='text-xs text-brand-gray'>
                          The status will update automatically when the MSA is signed.
                        </p>
                      </CardContent>
                    </Card>
                  )}

                  {/* MSA: Signed */}
                  {rfcStatus === RfcStatus.MSA_SIGNED && rfc && (
                    <Card className='border-gray-200 shadow-sm'>
                      <CardHeader className='bg-gray-50 border-b'>
                        <CardTitle className='text-lg font-semibold text-brand-navy'>
                          MSA Status
                        </CardTitle>
                      </CardHeader>
                      <CardContent className='space-y-4 p-6'>
                        <div className='flex items-center gap-2'>
                          <CheckCircle className='h-5 w-5 text-emerald' />
                          <span className='text-sm font-medium text-brand-navy'>
                            {rfc.msaManualUrl ? 'MSA Submitted Manually' : 'MSA Signed'}
                          </span>
                        </div>
                        <Separator />
                        <div className='space-y-2 text-sm'>
                          {rfc.msaManualUrl && (
                            <div className='flex justify-between items-start'>
                              <span className='text-brand-gray'>MSA URL:</span>
                              <a
                                href={rfc.msaManualUrl}
                                target='_blank'
                                rel='noopener noreferrer'
                                className='text-brand-blue hover:underline text-right max-w-[60%] break-all'
                              >
                                {rfc.msaManualUrl}
                              </a>
                            </div>
                          )}
                          {rfc.msaManualNote && (
                            <div className='flex justify-between items-start'>
                              <span className='text-brand-gray'>Note:</span>
                              <span className='text-brand-navy text-right max-w-[60%]'>
                                {rfc.msaManualNote}
                              </span>
                            </div>
                          )}
                          {rfc.msaSentAt && !rfc.msaManualUrl && (
                            <div className='flex justify-between'>
                              <span className='text-brand-gray'>Sent:</span>
                              <span className='text-brand-navy'>
                                {format(new Date(rfc.msaSentAt), 'MMM d, yyyy')}
                              </span>
                            </div>
                          )}
                          {rfc.msaSignedAt && (
                            <div className='flex justify-between'>
                              <span className='text-brand-gray'>
                                {rfc.msaManualUrl ? 'Submitted:' : 'Signed:'}
                              </span>
                              <span className='text-brand-navy'>
                                {format(new Date(rfc.msaSignedAt), 'MMM d, yyyy')}
                              </span>
                            </div>
                          )}
                        </div>
                        {isInternalUser && (
                          <>
                            <Separator />
                            <Button
                              className='w-full bg-blue-600 hover:bg-blue-700 text-white'
                              onClick={() =>
                                navigate(
                                  `/onboarding?startOnboarding=true&rfcId=${rfc.id}&clientId=${rfc.clientId}&groupName=${encodeURIComponent(rfc.companyName || '')}`,
                                )
                              }
                            >
                              <Rocket className='h-4 w-4 mr-2' />
                              Start Onboarding
                            </Button>
                          </>
                        )}
                        {!isInternalUser && !isBrokerUser && (
                          <>
                            <Separator />
                            <Button
                              className='w-full bg-brand-blue hover:bg-brand-blue-dark text-white font-semibold'
                              onClick={() => notifyCbsMsaSignedMutation.mutate({rfcId: rfc.id})}
                              disabled={notifyCbsMsaSignedMutation.isPending}
                            >
                              {notifyCbsMsaSignedMutation.isPending ? (
                                <Loader2 className='h-4 w-4 mr-2 animate-spin' />
                              ) : (
                                <Send className='h-4 w-4 mr-2' />
                              )}
                              {notifyCbsMsaSignedMutation.isPending
                                ? 'Notifying...'
                                : 'Notify CBS Team'}
                            </Button>
                          </>
                        )}
                      </CardContent>
                    </Card>
                  )}

                  {/* Deal Access Link */}
                  {accessUrl && (
                    <Card className='border-gray-200 shadow-sm'>
                      <CardHeader className='bg-gray-50 border-b'>
                        <CardTitle className='text-lg font-semibold text-brand-navy flex items-center gap-2'>
                          <Link2 className='h-4 w-4 text-brand-blue' />
                          Deal Access Link
                        </CardTitle>
                      </CardHeader>
                      <CardContent className='space-y-4 p-6'>
                        <div className='space-y-1.5'>
                          <Label className='text-xs font-semibold text-brand-gray uppercase tracking-wider'>
                            Link
                          </Label>
                          <div className='flex gap-2'>
                            <Input
                              value={accessUrl}
                              readOnly
                              className='border-gray-200 text-sm font-mono'
                            />
                            <Button
                              type='button'
                              variant='outline'
                              size='icon'
                              onClick={handleCopyAccessUrl}
                              className='shrink-0 transition-colors'
                            >
                              {copied ? (
                                <CheckCircle className='h-4 w-4 text-emerald' />
                              ) : (
                                <Copy className='h-4 w-4' />
                              )}
                            </Button>
                          </div>
                        </div>

                        <div className='space-y-1.5'>
                          <Label className='text-xs font-semibold text-brand-gray uppercase tracking-wider'>
                            Password
                          </Label>
                          {newPassword ? (
                            <div className='flex items-center gap-2 p-3 bg-emerald/5 border border-emerald/20 rounded-md'>
                              <span className='font-mono font-bold text-brand-navy flex-1'>
                                {newPassword}
                              </span>
                              <Button
                                type='button'
                                variant='ghost'
                                size='icon'
                                className='h-7 w-7 shrink-0'
                                onClick={() => {
                                  navigator.clipboard.writeText(newPassword);
                                  toast({title: 'Password copied'});
                                }}
                              >
                                <Copy className='h-3.5 w-3.5' />
                              </Button>
                            </div>
                          ) : (
                            <p className='text-sm text-brand-gray'>
                              Generate a new password to share with the group.
                            </p>
                          )}
                          <Button
                            type='button'
                            variant='outline'
                            size='sm'
                            onClick={handleRegeneratePassword}
                            disabled={regeneratePasswordMutation.isPending}
                            className='w-full transition-colors'
                          >
                            <RefreshCcw className='h-3.5 w-3.5 mr-1.5' />
                            {regeneratePasswordMutation.isPending
                              ? 'Generating...'
                              : newPassword
                                ? 'Regenerate Password'
                                : 'Generate Password'}
                          </Button>
                        </div>

                        {newPassword && (
                          <>
                            <Separator />
                            <Button
                              type='button'
                              onClick={() => {
                                setShareLinkCopied(false);
                                setShareLinkOpen(true);
                              }}
                              className='w-full bg-brand-blue hover:bg-brand-blue-dark text-white font-semibold shadow-sm transition-colors'
                            >
                              <Share2 className='h-4 w-4 mr-2' />
                              Share Deal Link
                            </Button>
                          </>
                        )}
                      </CardContent>
                    </Card>
                  )}

                  {/* Internal: Complete Processing */}
                  {canComplete && (
                    <Card className='border-gray-200 shadow-sm'>
                      <CardHeader className='bg-gray-50 border-b'>
                        <CardTitle className='text-lg font-semibold text-brand-navy'>
                          Complete Processing
                        </CardTitle>
                      </CardHeader>
                      <CardContent className='space-y-4 p-6'>
                        <div>
                          <Label htmlFor='completionNotes' className='text-brand-navy'>
                            Completion Notes
                          </Label>
                          <Textarea
                            id='completionNotes'
                            placeholder='Final processing notes...'
                            value={completionNotes}
                            onChange={(e) => setCompletionNotes(e.target.value)}
                            rows={3}
                            className='border-gray-200 focus:border-brand-blue focus:ring-brand-blue'
                          />
                        </div>
                        <Button
                          onClick={handleComplete}
                          disabled={completeMutation.isPending}
                          className='w-full bg-emerald hover:bg-emerald/90 text-white font-semibold shadow-sm transition-colors'
                          size='lg'
                        >
                          <CheckCircle className='h-4 w-4 mr-2' />
                          {completeMutation.isPending ? 'Completing...' : 'Mark as Complete'}
                        </Button>
                      </CardContent>
                    </Card>
                  )}

                  {/* Processing Status (Internal) / Deal Pipeline (Broker) / Next Steps (Client) */}
                  {isInternalUser && rfc && (
                    <Card className='border-gray-200 shadow-sm'>
                      <CardHeader className='bg-gray-50 border-b'>
                        <CardTitle className='text-lg font-semibold text-brand-navy'>
                          Processing Status
                        </CardTitle>
                      </CardHeader>
                      <CardContent className='space-y-4 p-6'>
                        <div className='flex items-center gap-2'>
                          {getStatusIcon(rfcStatus!)}
                          <Badge variant={getStatusColor(rfcStatus!)}>
                            {getRfcStatusDisplayName(rfcStatus!)}
                          </Badge>
                        </div>
                        <Separator />
                        <div className='space-y-2 text-sm'>
                          {rfc.submittedAt && (
                            <div className='flex justify-between'>
                              <span className='text-brand-gray'>Submitted:</span>
                              <span className='text-brand-navy'>
                                {format(new Date(rfc.submittedAt), 'MMM d, yyyy')}
                              </span>
                            </div>
                          )}
                          {rfc.assignedBroker && (
                            <div className='flex justify-between'>
                              <span className='text-brand-gray'>Broker:</span>
                              <span className='text-brand-navy'>{rfc.assignedBroker.name}</span>
                            </div>
                          )}
                          {rfc.brokerApprovedAt && (
                            <div className='flex justify-between'>
                              <span className='text-brand-gray'>Broker Approved:</span>
                              <span className='text-brand-navy'>
                                {format(new Date(rfc.brokerApprovedAt), 'MMM d, yyyy')}
                              </span>
                            </div>
                          )}
                          {rfc.clientApprovedAt && (
                            <div className='flex justify-between'>
                              <span className='text-brand-gray'>Client Approved:</span>
                              <span className='text-brand-navy'>
                                {format(new Date(rfc.clientApprovedAt), 'MMM d, yyyy')}
                              </span>
                            </div>
                          )}
                          {rfc.msaSentAt && !rfc.msaManualUrl && (
                            <div className='flex justify-between'>
                              <span className='text-brand-gray'>MSA Sent:</span>
                              <span className='text-brand-navy'>
                                {format(new Date(rfc.msaSentAt), 'MMM d, yyyy')}
                              </span>
                            </div>
                          )}
                          {rfc.msaSignedAt && (
                            <div className='flex justify-between'>
                              <span className='text-brand-gray'>
                                {rfc.msaManualUrl ? 'MSA Submitted:' : 'MSA Signed:'}
                              </span>
                              <span className='text-brand-navy'>
                                {format(new Date(rfc.msaSignedAt), 'MMM d, yyyy')}
                              </span>
                            </div>
                          )}
                          {rfc.msaManualUrl && (
                            <div className='flex justify-between items-start'>
                              <span className='text-brand-gray'>MSA URL:</span>
                              <a
                                href={rfc.msaManualUrl}
                                target='_blank'
                                rel='noopener noreferrer'
                                className='text-brand-blue hover:underline text-right max-w-[60%] break-all text-sm'
                              >
                                View MSA
                              </a>
                            </div>
                          )}
                          {rfc.cbsProcessedAt && (
                            <div className='flex justify-between'>
                              <span className='text-brand-gray'>CBS Processed:</span>
                              <span className='text-brand-navy'>
                                {format(new Date(rfc.cbsProcessedAt), 'MMM d, yyyy')}
                              </span>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {(isBrokerUser || isClientAdmin) && rfcStatus && (
                    <DealPipelineCard rfcStatus={rfcStatus} clientName={clientName} />
                  )}

                  {(isBrokerUser || isClientAdmin) && rfc && (
                    <OnboardingPipelineCard rfcId={rfc.id} />
                  )}

                  {!isInternalUser && !isBrokerUser && !isClientAdmin && rfcStatus && (
                    <NextStepsCard rfcStatus={rfcStatus} />
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Confirmation Dialog */}
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmAction === 'REJECT' ? 'Confirm Rejection' : 'Confirm Approval'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmAction === 'REJECT'
                ? 'Are you sure you want to reject this deal? A rejection reason is required.'
                : isBrokerUser
                  ? 'Are you sure you want to approve this deal? It will be forwarded to the client admin for review.'
                  : clientCanApproveForBroker
                    ? "Are you sure you want to approve this deal on the broker's behalf? It will then be ready for client review."
                    : 'Are you sure you want to approve this deal?'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className={confirmAction === 'REJECT' ? 'bg-red-600 hover:bg-red-700 text-white' : ''}
              disabled={isPending || !confirmAction}
              onClick={async () => {
                if (!confirmAction) return;
                setConfirmOpen(false);
                // A client admin approving from BROKER_REVIEW drives the
                // broker-step mutation, not adjudication.
                if (isBrokerUser || clientCanApproveForBroker) {
                  await onBrokerAction(confirmAction);
                } else {
                  await onClientAdjudicate(confirmAction);
                }
                setConfirmAction(null);
              }}
            >
              {confirmAction === 'REJECT' ? 'Reject Deal' : 'Approve Deal'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Share Deal Link Dialog */}
      <Dialog open={shareLinkOpen} onOpenChange={setShareLinkOpen}>
        <DialogContent className='sm:max-w-lg'>
          <DialogHeader>
            <DialogTitle className='text-brand-navy flex items-center gap-2'>
              <Mail className='h-5 w-5 text-brand-blue' />
              Share Deal Link
            </DialogTitle>
          </DialogHeader>
          {(() => {
            const company =
              rfc?.companyName ||
              rfc?.section125FormData?.groupInfo?.companyName ||
              rfc?.mecFormData?.groupInfo?.companyName ||
              '';
            const companyLine = company ? ` for ${company}` : '';
            const shareMessage = `Hi,

We've set up a deal form${companyLine}. Please use the link below to complete your information:

Link: ${accessUrl}
Password: ${newPassword}

Once you've completed the form, we'll be notified and will follow up with next steps.

Thanks!`;
            return (
              <div className='space-y-4'>
                <p className='text-sm text-brand-gray'>
                  Copy this message and send it to the group. It includes the deal link and access
                  password.
                </p>
                <Textarea
                  value={shareMessage}
                  readOnly
                  rows={11}
                  className='border-gray-200 bg-gray-50 text-sm font-mono resize-none'
                />
                <div className='flex justify-end gap-2'>
                  <Button
                    variant='outline'
                    onClick={() => setShareLinkOpen(false)}
                    className='transition-colors'
                  >
                    Close
                  </Button>
                  <Button
                    onClick={() => {
                      navigator.clipboard.writeText(shareMessage);
                      setShareLinkCopied(true);
                      setTimeout(() => setShareLinkCopied(false), 2000);
                    }}
                    className='bg-brand-blue hover:bg-brand-blue-dark text-white font-semibold shadow-sm transition-colors'
                  >
                    <Copy className='h-4 w-4 mr-2' />
                    {shareLinkCopied ? 'Copied!' : 'Copy Message'}
                  </Button>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// --- Sub-components ---

function StatusInfoBanner({
  status,
  isInternalUser,
  isBrokerUser,
}: {
  status: RfcStatus | undefined;
  isInternalUser: boolean;
  isBrokerUser: boolean;
}) {
  if (!status) return null;
  if (status === RfcStatus.MSA_PENDING) {
    return (
      <Alert className='mb-6 border-2 border-amber bg-amber/10 shadow-md'>
        <AlertCircle className='h-6 w-6 text-amber' />
        <AlertTitle className='text-lg font-bold text-amber-900'>
          MSA Needs to Be Sent for Signing
        </AlertTitle>
        <AlertDescription className='text-base font-medium text-amber-800'>
          This deal is approved. An MSA needs to be sent for signing before processing can continue.
        </AlertDescription>
      </Alert>
    );
  }
  const messages: Partial<Record<RfcStatus, string>> = {
    [RfcStatus.BROKER_REVIEW]: 'This deal is awaiting broker review.',
    [RfcStatus.CLIENT_APPROVED]: 'This deal has been approved and is now being processed.',
    [RfcStatus.MSA_SENT]: 'The MSA has been sent via DocuSign and is awaiting signature.',
    [RfcStatus.MSA_SIGNED]: 'The MSA has been signed. Deal is ready for internal processing.',
    [RfcStatus.CBS_PROCESSING]: 'This deal is currently being processed by the CBS team.',
    [RfcStatus.COMPLETED]: 'This deal has been completed.',
    [RfcStatus.REJECTED]: 'This deal has been rejected.',
    [RfcStatus.DRAFT]: 'This deal is still in draft status and has not been submitted yet.',
  };
  const message = messages[status];
  if (!message) return null;
  return (
    <Alert className='mb-6 border-brand-blue/20 bg-brand-blue/5'>
      <AlertDescription className='text-brand-navy'>{message}</AlertDescription>
    </Alert>
  );
}

function InternalFieldsTab({
  rfcId,
  rfcStatus,
  internalData,
  internalForm,
  productCodes,
  appendProductCode,
  removeProductCode,
  canProcess,
  isCompleted,
  processingNotes,
  setProcessingNotes,
  onSave,
  isSaving,
}: {
  rfcId: string;
  rfcStatus: RfcStatus;
  internalData: InternalData | undefined;
  internalForm: ReturnType<typeof useForm<InternalData>>;
  productCodes: Array<{id: string; value: string}>;
  appendProductCode: (value: {value: string}) => void;
  removeProductCode: (index: number) => void;
  canProcess: boolean;
  isCompleted: boolean;
  processingNotes: string;
  setProcessingNotes: (v: string) => void;
  onSave: () => void;
  isSaving: boolean;
}) {
  return (
    <>
      <Alert className='border-brand-blue/20 bg-brand-blue/5'>
        <AlertDescription className='text-brand-navy'>
          <strong>Internal Use Only:</strong> These fields can only be edited by a CBS team member.
        </AlertDescription>
      </Alert>
      {!canProcess && !isCompleted && (
        <Alert className='border-amber/20 bg-amber/5'>
          <AlertDescription className='text-brand-navy'>
            The deal must be in{' '}
            <span className='font-bold'>{getRfcStatusDisplayName(RfcStatus.CLIENT_APPROVED)}</span>{' '}
            or{' '}
            <span className='font-bold'>{getRfcStatusDisplayName(RfcStatus.CBS_PROCESSING)}</span>{' '}
            status to edit internal fields.
          </AlertDescription>
        </Alert>
      )}
      {isCompleted ? (
        <Card className='border-gray-200 shadow-sm'>
          <CardContent className='space-y-8 p-6'>
            <div className='space-y-4'>
              <div className='font-semibold text-brand-navy'>Internal Fields</div>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                <ReadOnlyField
                  label='Product Codes'
                  value={
                    internalData?.productCodes?.length
                      ? internalData.productCodes.map((pc) => pc.value).join(', ')
                      : undefined
                  }
                />
                <ReadOnlyField
                  label='Carrier'
                  value={internalData?.carrierName?.replace(/_/g, ' ')}
                />
                {internalData?.carrierName === 'OTHER' && (
                  <ReadOnlyField label='Carrier (Other)' value={internalData?.carrierNameOther} />
                )}
                <ReadOnlyField
                  label='Telemedicine'
                  value={internalData?.telemedicine?.replace(/_/g, ' ')}
                />
                {internalData?.telemedicine === 'OTHER' && (
                  <ReadOnlyField
                    label='Telemedicine (Other)'
                    value={internalData?.telemedicineOther}
                  />
                )}
                <ReadOnlyField label='PBM' value={internalData?.pbm?.replace(/_/g, ' ')} />
                {internalData?.pbm === 'OTHER' && (
                  <ReadOnlyField label='PBM (Other)' value={internalData?.pbmOther} />
                )}
                <ReadOnlyField label='App' value={internalData?.app?.replace(/_/g, ' ')} />
                {internalData?.app === 'OTHER' && (
                  <ReadOnlyField label='App (Other)' value={internalData?.appOther} />
                )}
                <ReadOnlyField label='MEC' value={internalData?.mec?.replace(/_/g, ' ')} />
                {internalData?.mec === 'OTHER' && (
                  <ReadOnlyField label='MEC (Other)' value={internalData?.mecOther} />
                )}
                <ReadOnlyField
                  label='PPO Network'
                  value={internalData?.ppoNetwork?.replace(/_/g, ' ')}
                />
                {internalData?.ppoNetwork === 'OTHER' && (
                  <ReadOnlyField
                    label='PPO Network (Other)'
                    value={internalData?.ppoNetworkOther}
                  />
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className='border-gray-200 shadow-sm'>
          <CardHeader className='bg-gray-50 border-b'>
            <CardTitle className='text-lg font-semibold text-brand-navy'>
              Internal Configuration
            </CardTitle>
          </CardHeader>
          <CardContent className='space-y-6 p-6'>
            <Form {...internalForm}>
              <div className='space-y-8'>
                <div className='space-y-4'>
                  <div className='font-semibold text-brand-navy'>Internal Fields</div>
                  <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                    <div className='md:col-span-2'>
                      <FormItem>
                        <FormLabel className='text-brand-navy'>Product Codes</FormLabel>
                        <div className='space-y-2'>
                          {productCodes.map((field, index) => (
                            <div key={field.id} className='flex gap-2'>
                              <FormField
                                control={internalForm.control}
                                name={`productCodes.${index}.value`}
                                render={({field}) => (
                                  <FormItem className='flex-1'>
                                    <FormControl>
                                      <Input
                                        placeholder='Enter product code'
                                        {...field}
                                        disabled={!canProcess}
                                        className='border-gray-200 focus:border-brand-blue focus:ring-brand-blue'
                                      />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                              <Button
                                type='button'
                                variant='outline'
                                size='icon'
                                onClick={() => removeProductCode(index)}
                                disabled={!canProcess}
                                className='transition-colors'
                              >
                                <X className='h-4 w-4' />
                              </Button>
                            </div>
                          ))}
                          <Button
                            type='button'
                            variant='outline'
                            size='sm'
                            onClick={() => appendProductCode({value: ''})}
                            disabled={!canProcess}
                            className='transition-colors'
                          >
                            <Plus className='h-4 w-4 mr-2' />
                            Add Product Code
                          </Button>
                        </div>
                      </FormItem>
                    </div>
                    <InternalSelectField
                      form={internalForm}
                      name='carrierName'
                      label='Carrier'
                      canProcess={canProcess}
                      options={[
                        {value: 'AMERICAN_GLOBAL', label: 'American Global'},
                        {value: 'CLEAR_SPRING_HEALTH', label: 'Clear Spring Health'},
                        {value: 'SELF_FUNDED', label: 'Self Funded'},
                        {value: 'OTHER', label: 'Other'},
                      ]}
                    />
                    {internalForm.watch('carrierName') === 'OTHER' && (
                      <InternalTextField
                        form={internalForm}
                        name='carrierNameOther'
                        label='Carrier (Other)'
                        canProcess={canProcess}
                      />
                    )}
                    <InternalSelectField
                      form={internalForm}
                      name='telemedicine'
                      label='Telemedicine'
                      canProcess={canProcess}
                      options={[
                        {value: 'CLEVER_HEALTH', label: 'Clever Health'},
                        {value: 'OTHER', label: 'Other'},
                      ]}
                    />
                    {internalForm.watch('telemedicine') === 'OTHER' && (
                      <InternalTextField
                        form={internalForm}
                        name='telemedicineOther'
                        label='Telemedicine (Other)'
                        canProcess={canProcess}
                      />
                    )}
                    <InternalSelectField
                      form={internalForm}
                      name='pbm'
                      label='PBM'
                      canProcess={canProcess}
                      options={[
                        {value: 'C3RX', label: 'C3RX'},
                        {value: 'VERUS', label: 'Verus'},
                        {value: 'OTHER', label: 'Other'},
                      ]}
                    />
                    {internalForm.watch('pbm') === 'OTHER' && (
                      <InternalTextField
                        form={internalForm}
                        name='pbmOther'
                        label='PBM (Other)'
                        canProcess={canProcess}
                      />
                    )}
                    <InternalSelectField
                      form={internalForm}
                      name='app'
                      label='App'
                      canProcess={canProcess}
                      options={[
                        {value: 'CLEVER', label: 'Clever'},
                        {value: 'OTHER', label: 'Other'},
                      ]}
                    />
                    {internalForm.watch('app') === 'OTHER' && (
                      <InternalTextField
                        form={internalForm}
                        name='appOther'
                        label='App (Other)'
                        canProcess={canProcess}
                      />
                    )}
                    <InternalSelectField
                      form={internalForm}
                      name='mec'
                      label='MEC'
                      canProcess={canProcess}
                      options={[
                        {value: 'CHAMPION_HEALTH', label: 'Champion Health MEC'},
                        {value: 'CHAMPION_HEALTH_HSA', label: 'Champion Health HSA MEC'},
                        {value: 'HEALTHCUES', label: 'HealthCues MEC'},
                        {value: 'QUANTUM', label: 'Quantum MEC'},
                        {value: 'OTHER', label: 'Other'},
                      ]}
                    />
                    {internalForm.watch('mec') === 'OTHER' && (
                      <InternalTextField
                        form={internalForm}
                        name='mecOther'
                        label='MEC (Other)'
                        canProcess={canProcess}
                      />
                    )}
                    <InternalSelectField
                      form={internalForm}
                      name='ppoNetwork'
                      label='PPO Network'
                      canProcess={canProcess}
                      options={[
                        {value: 'FIRST_HEALTH', label: 'First Health'},
                        {value: 'PHCS', label: 'PHCS'},
                        {value: 'OTHER', label: 'Other'},
                      ]}
                    />
                    {internalForm.watch('ppoNetwork') === 'OTHER' && (
                      <InternalTextField
                        form={internalForm}
                        name='ppoNetworkOther'
                        label='PPO Network (Other)'
                        canProcess={canProcess}
                      />
                    )}
                  </div>
                </div>
                <Separator />
                <div className='space-y-2'>
                  <Label htmlFor='processingNotes' className='text-brand-navy'>
                    Processing Notes
                  </Label>
                  <Textarea
                    id='processingNotes'
                    placeholder='Add processing notes...'
                    value={processingNotes}
                    onChange={(e) => setProcessingNotes(e.target.value)}
                    rows={3}
                    disabled={!canProcess}
                    className='border-gray-200 focus:border-brand-blue focus:ring-brand-blue'
                  />
                </div>
                <div className='flex justify-end'>
                  <Button
                    type='button'
                    onClick={onSave}
                    disabled={!canProcess || isSaving}
                    className='bg-brand-blue hover:bg-brand-blue-dark text-white font-semibold shadow-sm transition-colors'
                  >
                    <Save className='h-4 w-4 mr-2' />
                    {isSaving ? 'Saving...' : 'Save Changes'}
                  </Button>
                </div>
              </div>
            </Form>
          </CardContent>
        </Card>
      )}
    </>
  );
}

function InternalSelectField({
  form,
  name,
  label,
  canProcess,
  options,
}: {
  form: ReturnType<typeof useForm<InternalData>>;
  name: keyof InternalData;
  label: string;
  canProcess: boolean;
  options: Array<{value: string; label: string}>;
}) {
  return (
    <FormField
      control={form.control}
      name={name}
      render={({field}) => (
        <FormItem>
          <FormLabel className='text-brand-navy'>{label}</FormLabel>
          <Select
            onValueChange={field.onChange}
            defaultValue={field.value as string}
            disabled={!canProcess}
          >
            <FormControl>
              <SelectTrigger className='border-gray-200 focus:border-brand-blue focus:ring-brand-blue'>
                <SelectValue placeholder={`Select ${label.toLowerCase()}`} />
              </SelectTrigger>
            </FormControl>
            <SelectContent>
              {options.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

function InternalTextField({
  form,
  name,
  label,
  canProcess,
}: {
  form: ReturnType<typeof useForm<InternalData>>;
  name: keyof InternalData;
  label: string;
  canProcess: boolean;
}) {
  return (
    <FormField
      control={form.control}
      name={name}
      render={({field}) => (
        <FormItem>
          <FormLabel className='text-brand-navy'>{label}</FormLabel>
          <FormControl>
            <Input
              placeholder={`Specify ${label.toLowerCase()}`}
              {...field}
              value={field.value as string}
              disabled={!canProcess}
              className='border-gray-200 focus:border-brand-blue focus:ring-brand-blue'
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

function ReadOnlyField({label, value}: {label: string; value: string | undefined}) {
  return (
    <div>
      <Label className='text-brand-navy'>{label}</Label>
      <div className='mt-1 text-sm text-brand-gray'>{value || '\u2014'}</div>
    </div>
  );
}

function SummaryTab({
  rfc,
  rfcStatus,
}: {
  rfc: NonNullable<ReturnType<typeof trpc.clients.rfc.getById.useQuery>['data']>;
  rfcStatus: RfcStatus;
}) {
  return (
    <div className='space-y-6'>
      <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
        <Card className='border-gray-200 shadow-sm'>
          <CardContent className='pt-6'>
            <div className='flex items-center space-x-2'>
              <Building className='h-5 w-5 text-brand-gray' />
              <p className='text-sm font-medium text-brand-gray'>Company</p>
            </div>
            <p className='text-lg font-bold text-brand-navy mt-4'>
              {rfc.companyName ||
                rfc.section125FormData?.groupInfo.companyName ||
                rfc.mecFormData?.groupInfo.companyName ||
                'No company name'}
            </p>
          </CardContent>
        </Card>
        <Card className='border-gray-200 shadow-sm'>
          <CardContent className='pt-6'>
            <div className='flex items-center gap-2'>
              <User className='h-5 w-5 text-brand-gray' />
              <p className='text-sm font-medium text-brand-gray'>Employees</p>
            </div>
            <p className='text-lg font-bold text-brand-navy mt-4'>
              {rfc.section125FormData?.groupInfo.totalEmployees ||
                rfc.mecFormData?.groupInfo.totalEmployees ||
                0}
            </p>
          </CardContent>
        </Card>
        <Card className='border-gray-200 shadow-sm'>
          <CardContent className='pt-6'>
            <div className='flex items-center space-x-2'>
              <Clock className='h-5 w-5 text-brand-gray' />
              <p className='text-sm font-medium text-brand-gray'>RFC Age</p>
            </div>
            <p className='text-lg font-bold text-brand-navy mt-4'>
              {formatDistanceToNowStrict(new Date(rfc.createdAt))}
            </p>
            <p className='text-xs text-brand-gray mt-1'>
              Created {format(new Date(rfc.createdAt), 'MMM d, yyyy')}
            </p>
          </CardContent>
        </Card>
      </div>
      <Card className='border-gray-200 shadow-sm'>
        <CardHeader className='bg-gray-50 border-b'>
          <CardTitle className='text-lg font-semibold text-brand-navy'>
            Processing Checklist
          </CardTitle>
        </CardHeader>
        <CardContent className='p-6'>
          <div className='space-y-3'>
            <ChecklistItem label='Deal submitted' done={rfcStatus !== RfcStatus.DRAFT} />
            {rfc.assignedBroker && (
              <ChecklistItem
                label={`Broker approved (${rfc.assignedBroker.name})`}
                done={[
                  RfcStatus.BROKER_APPROVED,
                  RfcStatus.CLIENT_APPROVED,
                  RfcStatus.MSA_PENDING,
                  RfcStatus.MSA_SENT,
                  RfcStatus.MSA_SIGNED,
                  RfcStatus.CBS_PROCESSING,
                  RfcStatus.COMPLETED,
                ].includes(rfcStatus)}
              />
            )}
            <ChecklistItem
              label='Client admin approval received'
              done={[
                RfcStatus.CLIENT_APPROVED,
                RfcStatus.MSA_PENDING,
                RfcStatus.MSA_SENT,
                RfcStatus.MSA_SIGNED,
                RfcStatus.CBS_PROCESSING,
                RfcStatus.COMPLETED,
              ].includes(rfcStatus)}
            />
            <ChecklistItem
              label='MSA signed'
              done={[RfcStatus.MSA_SIGNED, RfcStatus.CBS_PROCESSING, RfcStatus.COMPLETED].includes(
                rfcStatus,
              )}
            />
            <ChecklistItem
              label='Internal processing completed'
              done={rfcStatus === RfcStatus.COMPLETED}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function ChecklistItem({label, done}: {label: string; done: boolean}) {
  return (
    <div className='flex items-center space-x-3'>
      <CheckCircle className={`h-5 w-5 ${done ? 'text-emerald' : 'text-gray-300'}`} />
      <span className={done ? 'text-brand-navy font-medium' : 'text-brand-gray'}>{label}</span>
    </div>
  );
}

function DealPipelineCard({rfcStatus, clientName}: {rfcStatus: RfcStatus; clientName: string}) {
  const statusOrder = [
    RfcStatus.DRAFT,
    RfcStatus.SUBMITTED,
    RfcStatus.BROKER_REVIEW,
    RfcStatus.BROKER_APPROVED,
    RfcStatus.CLIENT_REVIEW,
    RfcStatus.CLIENT_APPROVED,
    RfcStatus.MSA_PENDING,
    RfcStatus.MSA_SENT,
    RfcStatus.MSA_SIGNED,
    RfcStatus.CBS_PROCESSING,
    RfcStatus.COMPLETED,
  ];
  const currentIndex = statusOrder.indexOf(rfcStatus);
  const steps = [
    {status: RfcStatus.SUBMITTED, label: 'Submitted'},
    {status: RfcStatus.BROKER_REVIEW, label: 'Broker Review'},
    {status: RfcStatus.BROKER_APPROVED, label: 'Broker Approved'},
    {status: RfcStatus.CLIENT_APPROVED, label: `${clientName} Approved`},
    {status: RfcStatus.MSA_SIGNED, label: 'MSA Signed'},
    {status: RfcStatus.COMPLETED, label: 'Completed'},
  ];

  return (
    <Card className='border-gray-200 shadow-sm'>
      <CardHeader className='bg-gray-50 border-b'>
        <CardTitle className='text-lg font-semibold text-brand-navy'>Deal Pipeline</CardTitle>
      </CardHeader>
      <CardContent className='p-6'>
        <div className='space-y-3'>
          {steps.map((step) => {
            const stepIndex = statusOrder.indexOf(step.status);
            const isComplete = stepIndex <= currentIndex;
            return (
              <div key={step.status} className='flex items-center space-x-3'>
                <CheckCircle
                  className={`h-5 w-5 ${isComplete ? 'text-emerald' : 'text-gray-300'}`}
                />
                <span className={isComplete ? 'font-medium text-brand-navy' : 'text-brand-gray'}>
                  {step.label}
                </span>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

function NextStepsCard({rfcStatus}: {rfcStatus: RfcStatus}) {
  const stepsMap: Partial<Record<RfcStatus, string[]>> = {
    [RfcStatus.BROKER_REVIEW]: [
      'Waiting for broker to review and approve',
      'You will be notified when broker completes review',
    ],
    [RfcStatus.SUBMITTED]: [
      'Review all submitted information',
      'Check attached documents',
      'Approve or reject the deal',
    ],
    [RfcStatus.BROKER_APPROVED]: [
      'Review all submitted information',
      'Check attached documents',
      'Approve or reject the deal',
    ],
    [RfcStatus.MSA_PENDING]: [
      'Send MSA via DocuSign for signing',
      'Select a DocuSign template and specify signers',
    ],
    [RfcStatus.MSA_SENT]: [
      'MSA sent via DocuSign, awaiting signature',
      'Status will update automatically when signed',
    ],
    [RfcStatus.MSA_SIGNED]: [
      'MSA signed, deal ready for internal processing',
      'CBS team will process the deal',
    ],
    [RfcStatus.CLIENT_APPROVED]: [
      'Deal forwarded to CBS team',
      'CBS will process internal fields',
      'Company will be notified of completion',
    ],
  };
  const steps = stepsMap[rfcStatus];
  if (!steps) return null;

  return (
    <Card className='border-gray-200 shadow-sm'>
      <CardHeader className='bg-gray-50 border-b'>
        <CardTitle className='text-lg font-semibold text-brand-navy'>Next Steps</CardTitle>
      </CardHeader>
      <CardContent className='p-6'>
        <div className='space-y-1 text-sm'>
          {steps.map((step, i) => (
            <p key={i} className='text-brand-gray'>
              &bull; {step}
            </p>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

type RfcData = NonNullable<ReturnType<typeof trpc.clients.rfc.getById.useQuery>['data']>;

function ProposalTab({
  rfcId,
  proposals,
  companyName,
  onProposalSaved,
}: {
  rfcId: string;
  proposals: RfcData['proposals'];
  companyName: string;
  onProposalSaved: () => void;
}) {
  const fissionUrlQuery = trpc.clients.rfc.getFissionUrlForDeal.useQuery({rfcId});
  const saveProposalMutation = trpc.clients.rfc.saveProposal.useMutation({
    onError: createMutationErrorHandler(),
  });
  const deleteProposalMutation = trpc.clients.rfc.deleteProposal.useMutation({
    onError: createMutationErrorHandler(),
  });

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [showGenerator, setShowGenerator] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [shareProposal, setShareProposal] = useState<RfcData['proposals'][number] | null>(null);
  const [messageCopied, setMessageCopied] = useState(false);
  const [copiedField, setCopiedField] = useState<{id: string; type: 'url' | 'password'} | null>(
    null,
  );

  const copyProposalField = async (id: string, type: 'url' | 'password', text: string) => {
    await navigator.clipboard.writeText(text);
    setCopiedField({id, type});
    setTimeout(() => setCopiedField(null), 2000);
  };

  const stableUrl = fissionUrlQuery.data?.url ?? '';

  // Only honored when the event originates from the verified Fission embed
  // origin returned alongside the generator URL.
  useFissionMessage(fissionUrlQuery.data?.embedOrigin, {
    onProposalSaved: async ({url, password, label}) => {
      if (!password) return;

      await saveProposalMutation.mutateAsync({rfcId, url, password, label});
      setShowGenerator(false);
      toast({title: 'Proposal saved', description: 'The proposal has been saved to this deal.'});
      onProposalSaved();
    },
  });

  const handleDeleteProposal = async (proposalId: string) => {
    await deleteProposalMutation.mutateAsync({rfcId, proposalId});
    setConfirmDeleteId(null);
    toast({title: 'Proposal deleted'});
    onProposalSaved();
  };

  const generateShareMessage = (proposal: RfcData['proposals'][number]) => {
    const label = proposal.label || 'your benefits proposal';
    return `Hi,

I've put together ${label} for ${companyName}. You can review the details using the link below:

Link: ${proposal.url}
Password: ${proposal.password}

Let me know if you have any questions or would like to discuss further.

Thanks!`;
  };

  const handleCopyMessage = () => {
    if (!shareProposal) return;
    navigator.clipboard.writeText(generateShareMessage(shareProposal));
    setMessageCopied(true);
    setTimeout(() => setMessageCopied(false), 2000);
  };

  const generatorAvailable = !!stableUrl;
  const generatorNotConfigured = fissionUrlQuery.data && !fissionUrlQuery.data.url;

  return (
    <div className='space-y-6'>
      {/* Generator Section */}
      {showGenerator ? (
        <div className='space-y-3'>
          <div className='flex items-center justify-between'>
            <div className='flex items-center gap-2'>
              <Sparkles className='h-5 w-5 text-brand-blue' />
              <h3 className='text-lg font-semibold text-brand-navy'>Generating Proposal</h3>
            </div>
            <Button
              variant='outline'
              size='sm'
              onClick={() => setShowGenerator(false)}
              className='transition-colors'
            >
              <X className='h-4 w-4 mr-2' />
              Close Generator
            </Button>
          </div>
          {fissionUrlQuery.isLoading ? (
            <Card className='border-gray-200 shadow-sm'>
              <CardContent className='flex items-center justify-center py-20'>
                <div className='flex flex-col items-center gap-3'>
                  <Loader2 className='h-8 w-8 animate-spin text-brand-blue' />
                  <p className='text-sm text-brand-gray'>Loading proposal generator...</p>
                </div>
              </CardContent>
            </Card>
          ) : generatorAvailable ? (
            <div className='border border-gray-200 rounded-lg overflow-hidden shadow-sm'>
              <iframe
                ref={iframeRef}
                key={stableUrl}
                src={stableUrl}
                className='w-full border-0'
                style={{height: '700px'}}
                title='Proposal Generator'
              />
            </div>
          ) : (
            <Alert>
              <AlertCircle className='h-4 w-4' />
              <AlertTitle>Proposal generator not configured</AlertTitle>
              <AlertDescription>
                The proposal generator is not configured for this client. Contact support for
                assistance.
              </AlertDescription>
            </Alert>
          )}
          {saveProposalMutation.isPending && (
            <Alert className='border-brand-blue/20 bg-brand-blue/5'>
              <Loader2 className='h-4 w-4 animate-spin' />
              <AlertDescription className='text-brand-navy'>Saving proposal...</AlertDescription>
            </Alert>
          )}
        </div>
      ) : (
        <>
          {/* Header with Create button */}
          <div className='flex items-center justify-between'>
            <div>
              <h3 className='text-lg font-semibold text-brand-navy'>
                Proposals ({proposals.length})
              </h3>
              <p className='text-sm text-brand-gray mt-0.5'>
                Generate and manage proposals for this deal
              </p>
            </div>
            {generatorAvailable && (
              <Button
                onClick={() => setShowGenerator(true)}
                className='bg-brand-blue hover:bg-brand-blue-dark text-white font-semibold shadow-sm transition-colors'
              >
                <Plus className='h-4 w-4 mr-2' />
                New Proposal
              </Button>
            )}
            {generatorNotConfigured && (
              <Badge className='bg-amber/10 text-amber border-amber/20 text-xs font-medium'>
                Generator not configured
              </Badge>
            )}
          </div>

          {/* Proposals Grid */}
          {proposals.length === 0 ? (
            <Card className='border-gray-200 shadow-sm border-dashed'>
              <CardContent className='flex flex-col items-center justify-center py-16'>
                <div className='p-4 bg-brand-blue/5 rounded-full mb-4'>
                  <FileText className='h-10 w-10 text-brand-blue/40' />
                </div>
                <h4 className='text-lg font-semibold text-brand-navy mb-1'>No proposals yet</h4>
                <p className='text-sm text-brand-gray mb-6 max-w-sm text-center'>
                  Create your first proposal using the proposal generator to get started.
                </p>
                {generatorAvailable && (
                  <Button
                    onClick={() => setShowGenerator(true)}
                    className='bg-brand-blue hover:bg-brand-blue-dark text-white font-semibold shadow-sm transition-colors'
                  >
                    <Sparkles className='h-4 w-4 mr-2' />
                    Generate First Proposal
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
              {proposals.map((proposal, index) => (
                <Card
                  key={proposal.id}
                  className='border-gray-200 shadow-sm hover:shadow-md transition-shadow group'
                >
                  <CardContent className='p-5'>
                    <div className='flex items-start justify-between mb-3'>
                      <div className='flex items-center gap-3'>
                        <div className='p-2 bg-brand-blue/10 rounded-lg'>
                          <FileText className='h-5 w-5 text-brand-blue' />
                        </div>
                        <div>
                          <h4 className='font-semibold text-brand-navy'>
                            {proposal.label || `Proposal #${proposals.length - index}`}
                          </h4>
                          <div className='flex items-center gap-1.5 mt-0.5'>
                            <Clock className='h-3 w-3 text-brand-gray' />
                            <span className='text-xs text-brand-gray'>
                              {format(new Date(proposal.createdAt), 'MMM d, yyyy h:mm a')}
                            </span>
                          </div>
                        </div>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant='ghost'
                            size='icon'
                            className='h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity'
                          >
                            <MoreVertical className='h-4 w-4' />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align='end'>
                          <DropdownMenuItem
                            className='text-red-600'
                            onSelect={(e) => {
                              e.preventDefault();
                              setConfirmDeleteId(proposal.id);
                            }}
                          >
                            <Trash2 className='h-4 w-4' />
                            Delete Proposal
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                    <div className='flex items-center gap-1.5 text-xs text-brand-gray mb-3'>
                      <User className='h-3 w-3' />
                      <span>
                        {proposal.createdByUser?.name || proposal.createdByName || 'Unknown'}
                      </span>
                    </div>

                    {/* URL & Password visible */}
                    <div className='space-y-2 mb-4'>
                      <div className='flex gap-2'>
                        <a
                          href={proposal.url}
                          target='_blank'
                          rel='noopener noreferrer'
                          className='flex-1 flex items-center px-3 py-2 text-xs font-mono rounded-md border border-gray-200 bg-background text-brand-blue hover:underline truncate min-w-0'
                        >
                          <span className='truncate'>{proposal.url}</span>
                        </a>
                        <Button
                          type='button'
                          variant='outline'
                          size='sm'
                          onClick={() => copyProposalField(proposal.id, 'url', proposal.url)}
                          className='shrink-0 transition-colors'
                        >
                          {copiedField?.id === proposal.id && copiedField?.type === 'url' ? (
                            <CheckCircle className='h-4 w-4 text-emerald' />
                          ) : (
                            <Copy className='h-4 w-4' />
                          )}
                        </Button>
                      </div>
                      <div className='flex gap-2'>
                        <Input
                          value={proposal.password}
                          readOnly
                          className='text-xs font-mono border-gray-200'
                        />
                        <Button
                          type='button'
                          variant='outline'
                          size='sm'
                          onClick={() =>
                            copyProposalField(proposal.id, 'password', proposal.password)
                          }
                          className='shrink-0 transition-colors'
                        >
                          {copiedField?.id === proposal.id && copiedField?.type === 'password' ? (
                            <CheckCircle className='h-4 w-4 text-emerald' />
                          ) : (
                            <Copy className='h-4 w-4' />
                          )}
                        </Button>
                      </div>
                    </div>

                    <Button
                      variant='outline'
                      size='sm'
                      onClick={() => {
                        setShareProposal(proposal);
                        setMessageCopied(false);
                      }}
                      className='w-full transition-colors'
                    >
                      <Send className='h-3.5 w-3.5 mr-1.5' />
                      Share Proposal
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </>
      )}

      {/* Share message dialog */}
      <Dialog open={!!shareProposal} onOpenChange={(open) => !open && setShareProposal(null)}>
        <DialogContent className='sm:max-w-lg'>
          <DialogHeader>
            <DialogTitle className='text-brand-navy'>Share Proposal</DialogTitle>
          </DialogHeader>
          {shareProposal && (
            <div className='space-y-4'>
              <p className='text-sm text-brand-gray'>
                Copy this message and send it to your client. It includes the proposal link and
                password.
              </p>
              <div className='relative'>
                <Textarea
                  value={generateShareMessage(shareProposal)}
                  readOnly
                  rows={10}
                  className='border-gray-200 bg-gray-50 text-sm font-mono resize-none pr-4'
                />
              </div>
              <div className='flex justify-end gap-2'>
                <Button
                  variant='outline'
                  onClick={() => setShareProposal(null)}
                  className='transition-colors'
                >
                  Close
                </Button>
                <Button
                  onClick={handleCopyMessage}
                  className='bg-brand-blue hover:bg-brand-blue-dark text-white font-semibold shadow-sm transition-colors'
                >
                  <Copy className='h-4 w-4 mr-2' />
                  {messageCopied ? 'Copied!' : 'Copy Message'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete confirmation dialog */}
      <AlertDialog
        open={!!confirmDeleteId}
        onOpenChange={(open) => !open && setConfirmDeleteId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this proposal?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove the proposal. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteProposalMutation.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className='bg-red-600 hover:bg-red-700 text-white'
              onClick={() => confirmDeleteId && handleDeleteProposal(confirmDeleteId)}
              disabled={deleteProposalMutation.isPending}
            >
              {deleteProposalMutation.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
